import { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import SearchableSelect from "@/components/SearchableSelect";
import ExplorerTour from "@/components/ExplorerTour";
import { FOOTBALL_POSITIONS, COUNTRIES, PROFESSIONS } from "@/lib/constants";
import { Search, MapPin, Briefcase, Filter, UserPlus, ChevronLeft, ChevronRight, X, ShieldCheck, ChevronDown, Crown, Compass, LayoutDashboard, Eye } from "lucide-react";
import { toast } from "sonner";
import FavoriteButton from "@/components/FavoriteButton";

/* --------- Filter Category (collapsible) --------- */
function FilterCategory({ title, defaultOpen = true, children, testId }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-[#E6E6E6] last:border-0" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between py-2.5 text-left"
        data-testid={testId ? `${testId}-toggle` : undefined}
      >
        <span className="text-xs font-bold text-zinc-800 uppercase tracking-wider">{title}</span>
        <ChevronDown size={14} className={`text-zinc-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="pb-3 space-y-3">{children}</div>}
    </div>
  );
}

/* --------- Premium Gate Dialog --------- */
function PremiumGate({ open, onClose }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
            <Crown size={18} className="text-amber-500" /> Unlock Scout Dashboard
          </DialogTitle>
        </DialogHeader>
        <div className="text-center py-4" data-testid="premium-gate-content">
          <div className="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-4">
            <LayoutDashboard size={26} className="text-amber-500" />
          </div>
          <p className="text-sm text-zinc-700">
            The Scout Dashboard is a <span className="font-semibold text-[#074572]">Premium</span> feature.
          </p>
          <ul className="text-left text-xs text-zinc-600 mt-4 space-y-1.5 max-w-xs mx-auto">
            <li className="flex gap-2"><span className="text-emerald-500">✓</span> Shortlist and compare up to 8 athletes side-by-side</li>
            <li className="flex gap-2"><span className="text-emerald-500">✓</span> Radar + per-metric graphs vs peer average</li>
            <li className="flex gap-2"><span className="text-emerald-500">✓</span> Save filter combinations for one-click re-runs</li>
            <li className="flex gap-2"><span className="text-emerald-500">✓</span> Ergometric percentiles + CSV export</li>
          </ul>
        </div>
        <div className="flex justify-end gap-2 border-t border-[#E6E6E6] pt-3">
          <Button variant="outline" onClick={onClose} className="border-[#E6E6E6] h-8 text-xs" data-testid="premium-gate-close">Not now</Button>
          <Link to="/premium">
            <Button className="bg-[#F85037] hover:bg-[#e04530] text-white h-8 text-xs gap-1.5" data-testid="premium-gate-upgrade">
              <Crown size={12} /> Upgrade to Premium
            </Button>
          </Link>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function ScoutNavigatorPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("view") === "dashboard" ? "dashboard" : "discover";
  const [proEnabled, setProEnabled] = useState(false);
  const [gateOpen, setGateOpen] = useState(false);

  useEffect(() => {
    api.get("/platform/settings").then(r => setProEnabled(!!r.data?.pro_enabled)).catch(() => {});
  }, []);

  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [role, setRole] = useState("");
  const [position, setPosition] = useState("");
  const [profession, setProfession] = useState("");
  const [location, setLocation] = useState("");
  const [minSprint, setMinSprint] = useState("");
  const [minVo2, setMinVo2] = useState("");
  const [minJump, setMinJump] = useState("");
  const [maxAgility, setMaxAgility] = useState("");
  const [minEndurance, setMinEndurance] = useState("");
  const [ergoVerified, setErgoVerified] = useState(false);
  const [openToOpp, setOpenToOpp] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const fetchResults = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (role) params.set("role", role);
      if (position) params.set("position", position);
      if (profession) params.set("profession", profession);
      if (location) params.set("location", location);
      if (minSprint) params.set("min_sprint", minSprint);
      if (minVo2) params.set("min_vo2", minVo2);
      if (minJump) params.set("min_jump", minJump);
      if (maxAgility) params.set("max_agility", maxAgility);
      if (minEndurance) params.set("min_endurance", minEndurance);
      if (ergoVerified) params.set("ergo_verified", "true");
      if (openToOpp) params.set("open_to_opportunities", "true");
      params.set("page", page);
      params.set("limit", 12);
      const { data } = await api.get(`/scout/discover?${params}`);
      setResults(data.users || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
    } catch { toast.error("Failed to load results"); }
    finally { setLoading(false); }
  }, [search, role, position, profession, location, page, minSprint, minVo2, minJump, maxAgility, minEndurance, ergoVerified, openToOpp]);

  useEffect(() => { if (activeTab === "discover") fetchResults(); }, [fetchResults, activeTab]);

  useEffect(() => {
    const urlSearch = searchParams.get("search") || "";
    setSearch(urlSearch);
    setPage(1);
  }, [searchParams]);

  const handleConnect = async (userId) => {
    try {
      await api.post(`/connections/request/${userId}`);
      toast.success("Connection request sent!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send request");
    }
  };

  const clearFilters = () => {
    setSearch(""); setRole(""); setPosition(""); setProfession(""); setLocation(""); setPage(1);
    setMinSprint(""); setMinVo2(""); setMinJump(""); setMaxAgility(""); setMinEndurance("");
    setErgoVerified(false); setOpenToOpp(false);
  };

  const hasFilters = search || role || position || profession || location || minSprint || minVo2 || minJump || maxAgility || minEndurance || ergoVerified || openToOpp;

  const handleTabClick = (v) => {
    if (v === "dashboard") {
      // If premium is enabled from admin & user is NOT premium → show gate
      if (proEnabled && !user?.is_premium) {
        setGateOpen(true);
        return;
      }
      navigate("/dashboard");
      return;
    }
    setSearchParams({ view: "discover" });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="scout-navigator-page">
      <ExplorerTour role={user?.role} />
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-medium text-zinc-900" style={{ fontFamily: 'Outfit' }}>Scout Navigator</h1>
          <p className="text-sm text-zinc-500 mt-1">Discover talent across the football world</p>
        </div>
        <Button
          variant="outline" className="gap-2 border-[#E6E6E6] md:hidden"
          onClick={() => setShowFilters(!showFilters)}
          data-testid="scout-filter-toggle"
        >
          <Filter size={16} /> Filters
        </Button>
      </div>

      {/* Top tabs: Discover | Dashboard */}
      <div className="flex items-center gap-1 border-b border-[#E6E6E6] mb-6" data-testid="scout-nav-tabs">
        <button
          onClick={() => handleTabClick("discover")}
          className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
            activeTab === "discover" ? "border-[#074572] text-[#074572]" : "border-transparent text-zinc-500 hover:text-[#074572]"
          }`}
          data-testid="scout-tab-discover"
        >
          <Compass size={14} /> Discover
        </button>
        <button
          onClick={() => handleTabClick("dashboard")}
          className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
            activeTab === "dashboard" ? "border-[#074572] text-[#074572]" : "border-transparent text-zinc-500 hover:text-[#074572]"
          }`}
          data-testid="scout-tab-dashboard"
        >
          <LayoutDashboard size={14} /> Scout Dashboard
          {proEnabled && !user?.is_premium && <Crown size={11} className="text-amber-500" />}
        </button>
      </div>

      <div className="flex gap-6">
        {/* Filters Sidebar */}
        <aside className={`${showFilters ? 'block' : 'hidden'} md:block w-full md:w-64 shrink-0`}>
          <div className="bg-white rounded-md border border-[#E6E6E6] p-4 sticky top-20" data-testid="scout-filters">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-zinc-900 uppercase tracking-wide" style={{ fontFamily: 'Outfit' }}>Filters</h3>
              {hasFilters && (
                <button onClick={clearFilters} className="text-xs text-[#F85037] hover:underline flex items-center gap-1" data-testid="scout-clear-filters">
                  <X size={12} /> Clear
                </button>
              )}
            </div>

            {/* Category 1: Basic */}
            <FilterCategory title="Basic" defaultOpen testId="filter-cat-basic">
              <div>
                <label className="text-[11px] font-medium text-zinc-500 mb-1 block">Search</label>
                <div className="relative">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                  <Input
                    placeholder="Name, skill..."
                    value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                    className="pl-9 border-[#E6E6E6] text-sm h-9"
                    data-testid="scout-search-input"
                  />
                </div>
              </div>
              <div>
                <label className="text-[11px] font-medium text-zinc-500 mb-1 block">Role</label>
                <Select value={role} onValueChange={(v) => { setRole(v === "all" ? "" : v); setPage(1); }}>
                  <SelectTrigger className="border-[#E6E6E6] h-9 text-sm" data-testid="scout-role-filter">
                    <SelectValue placeholder="All roles" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Roles</SelectItem>
                    <SelectItem value="athlete">Athlete</SelectItem>
                    <SelectItem value="scout">Scout</SelectItem>
                    <SelectItem value="coach">Coach</SelectItem>
                    <SelectItem value="explorer">Explorer</SelectItem>
                    <SelectItem value="professional">Professional</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {role === "professional" && (
                <div>
                  <label className="text-[11px] font-medium text-zinc-500 mb-1 block">Profession</label>
                  <SearchableSelect
                    value={profession}
                    onValueChange={(v) => { setProfession(v); setPage(1); }}
                    options={PROFESSIONS.filter((p) => p !== "Other")}
                    placeholder="All professions"
                    testId="scout-profession-filter"
                  />
                </div>
              )}
              <div>
                <label className="text-[11px] font-medium text-zinc-500 mb-1 block">Position</label>
                <SearchableSelect
                  value={position}
                  onValueChange={(v) => { setPosition(v); setPage(1); }}
                  options={FOOTBALL_POSITIONS}
                  placeholder="All positions"
                  testId="scout-position-filter"
                />
              </div>
              <div>
                <label className="text-[11px] font-medium text-zinc-500 mb-1 block">Country</label>
                <SearchableSelect
                  value={location}
                  onValueChange={(v) => { setLocation(v); setPage(1); }}
                  options={COUNTRIES}
                  placeholder="All countries"
                  testId="scout-country-filter"
                />
              </div>
            </FilterCategory>

            {/* Category 2: Ergometrics (bold dropdown) */}
            <FilterCategory title="Ergometrics" defaultOpen={false} testId="filter-cat-ergometrics">
              <div>
                <label className="text-[11px] font-medium text-zinc-500 mb-1 block">Min Sprint (km/h)</label>
                <Input type="number" step="0.1" placeholder="e.g. 30" value={minSprint} onChange={(e) => { setMinSprint(e.target.value); setPage(1); }} className="border-[#E6E6E6] text-sm h-9" data-testid="scout-min-sprint" />
              </div>
              <div>
                <label className="text-[11px] font-medium text-zinc-500 mb-1 block">Min VO2 Max</label>
                <Input type="number" step="0.1" placeholder="e.g. 50" value={minVo2} onChange={(e) => { setMinVo2(e.target.value); setPage(1); }} className="border-[#E6E6E6] text-sm h-9" data-testid="scout-min-vo2" />
              </div>
              <div>
                <label className="text-[11px] font-medium text-zinc-500 mb-1 block">Min Vertical Jump (cm)</label>
                <Input type="number" step="0.1" placeholder="e.g. 50" value={minJump} onChange={(e) => { setMinJump(e.target.value); setPage(1); }} className="border-[#E6E6E6] text-sm h-9" data-testid="scout-min-jump" />
              </div>
              <div>
                <label className="text-[11px] font-medium text-zinc-500 mb-1 block">Max Agility (sec)</label>
                <Input type="number" step="0.1" placeholder="e.g. 4.5" value={maxAgility} onChange={(e) => { setMaxAgility(e.target.value); setPage(1); }} className="border-[#E6E6E6] text-sm h-9" data-testid="scout-max-agility" />
              </div>
              <div>
                <label className="text-[11px] font-medium text-zinc-500 mb-1 block">Min Endurance (/100)</label>
                <Input type="number" step="0.1" placeholder="e.g. 80" value={minEndurance} onChange={(e) => { setMinEndurance(e.target.value); setPage(1); }} className="border-[#E6E6E6] text-sm h-9" data-testid="scout-min-endurance" />
              </div>
            </FilterCategory>

            {/* Category 3: Preferences (checkboxes) */}
            <FilterCategory title="Preferences" defaultOpen testId="filter-cat-preferences">
              <label className="flex items-center gap-2 cursor-pointer" data-testid="scout-verified-checkbox">
                <input
                  type="checkbox"
                  checked={ergoVerified}
                  onChange={(e) => { setErgoVerified(e.target.checked); setPage(1); }}
                  className="w-4 h-4 accent-[#074572]"
                />
                <span className="text-sm text-zinc-700 flex items-center gap-1">
                  <ShieldCheck size={13} className="text-[#074572]" /> Verified Only
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer" data-testid="scout-openopp-checkbox">
                <input
                  type="checkbox"
                  checked={openToOpp}
                  onChange={(e) => { setOpenToOpp(e.target.checked); setPage(1); }}
                  className="w-4 h-4 accent-[#074572]"
                />
                <span className="text-sm text-zinc-700 flex items-center gap-1">
                  <Eye size={13} className="text-emerald-600" /> Open to Opportunities
                </span>
              </label>
            </FilterCategory>
          </div>
        </aside>

        {/* Results Grid */}
        <div className="flex-1">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-zinc-500">{total} result{total !== 1 ? "s" : ""} found</p>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white border border-[#E6E6E6] rounded-md p-5 animate-pulse">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-full bg-zinc-100" />
                    <div className="flex-1"><div className="h-4 bg-zinc-100 rounded w-24 mb-1" /><div className="h-3 bg-zinc-100 rounded w-16" /></div>
                  </div>
                  <div className="h-3 bg-zinc-100 rounded w-full mb-2" />
                  <div className="h-3 bg-zinc-100 rounded w-3/4" />
                </div>
              ))}
            </div>
          ) : results.length === 0 ? (
            <div className="text-center py-16 text-zinc-400">
              <Search size={40} className="mx-auto mb-3 opacity-50" />
              <p className="font-medium">No results found</p>
              <p className="text-sm mt-1">Try adjusting your filters</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {results.map(u => {
                const initials = u.name?.split(" ").map(n => n[0]).join("").toUpperCase() || "?";
                const isMe = u._id === (user?._id || user?.id);
                return (
                  <div key={u._id} className="scout-card bg-white border border-[#E6E6E6] rounded-md p-5" data-testid={`scout-card-${u._id}`}>
                    <div className="flex items-start gap-3 mb-3">
                      <Link to={`/profile/${u._id}`}>
                        <Avatar className="h-12 w-12 border-2 border-[#E6E6E6]">
                          <AvatarFallback className="bg-[#074572] text-white font-medium">{initials}</AvatarFallback>
                        </Avatar>
                      </Link>
                      <div className="flex-1 min-w-0">
                        <Link to={`/profile/${u._id}`} className="font-medium text-zinc-900 hover:text-[#074572] text-sm block truncate">
                          {u.name}
                        </Link>
                        <div className="flex items-center gap-2 mt-0.5">
                          <Badge variant="secondary" className="text-[10px] bg-[#074572]/5 text-[#074572] capitalize">{u.role}</Badge>
                          {u.position && <span className="text-xs text-zinc-400">{u.position}</span>}
                          {u.ergo_verified && <ShieldCheck size={12} className="text-[#074572]" />}
                        </div>
                      </div>
                      {u.role === "athlete" && !isMe && <FavoriteButton athleteId={u._id} size={18} />}
                    </div>
                    {u.bio && <p className="text-xs text-zinc-500 line-clamp-2 mb-3">{u.bio}</p>}
                    <div className="flex items-center gap-3 text-xs text-zinc-400 mb-3">
                      {u.location && <span className="flex items-center gap-1"><MapPin size={12} />{u.location}</span>}
                      {u.club && <span className="flex items-center gap-1"><Briefcase size={12} />{u.club}</span>}
                    </div>
                    {u.skills?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-3">
                        {u.skills.slice(0, 3).map(s => (
                          <span key={s} className="text-[10px] px-2 py-0.5 bg-zinc-50 text-zinc-500 rounded-full border border-zinc-100">{s}</span>
                        ))}
                        {u.skills.length > 3 && <span className="text-[10px] text-zinc-400">+{u.skills.length - 3}</span>}
                      </div>
                    )}
                    <Separator className="bg-[#E6E6E6] mb-3" />
                    <div className="flex gap-2">
                      <Link to={`/profile/${u._id}`} className="flex-1">
                        <Button variant="outline" size="sm" className="w-full text-xs border-[#E6E6E6] text-[#074572]" data-testid={`scout-view-${u._id}`}>
                          View Profile
                        </Button>
                      </Link>
                      {!isMe && (
                        <Button
                          size="sm"
                          className="bg-[#F85037] hover:bg-[#e04530] text-white text-xs gap-1"
                          onClick={() => handleConnect(u._id)}
                          data-testid={`scout-connect-${u._id}`}
                        >
                          <UserPlus size={12} /> Connect
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {pages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-8">
              <Button
                variant="outline" size="sm" disabled={page <= 1}
                onClick={() => setPage(p => p - 1)}
                className="border-[#E6E6E6]"
                data-testid="scout-prev-page"
              >
                <ChevronLeft size={16} />
              </Button>
              <span className="text-sm text-zinc-500">Page {page} of {pages}</span>
              <Button
                variant="outline" size="sm" disabled={page >= pages}
                onClick={() => setPage(p => p + 1)}
                className="border-[#E6E6E6]"
                data-testid="scout-next-page"
              >
                <ChevronRight size={16} />
              </Button>
            </div>
          )}
        </div>
      </div>

      <PremiumGate open={gateOpen} onClose={() => setGateOpen(false)} />
    </div>
  );
}
